import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import ImportarCardapioDialog from "./ImportarCardapioDialog";
import { baixarPlanilha, enviarPlanilha, mensagemDeErro } from "./api";

/**
 * Botão "Planilha" da lista de Produtos (pedido do dono, 29/09/2026).
 *
 * Exportar baixa o cardápio inteiro com tudo do produto (etapas, promoção,
 * fiscal...). Importar lê a planilha de volta, mostra a prévia e só grava
 * depois da confirmação: preço, descrição, fiscal e produtos novos.
 */
export default function PlanilhaCardapio({ onImportado }) {
  const entrada = useRef(null);
  const [ocupado, setOcupado] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);

  const exportar = async () => {
    setOcupado(true);
    try {
      await baixarPlanilha();
    } catch (e) {
      toast.error(await mensagemDeErro(e, "Não foi possível gerar a planilha"));
    } finally {
      setOcupado(false);
    }
  };

  const escolher = async (e) => {
    const escolhido = e.target.files?.[0];
    // Limpa o input: escolher o mesmo arquivo de novo precisa disparar onChange.
    e.target.value = "";
    if (!escolhido) return;
    setOcupado(true);
    try {
      setPrevia(await enviarPlanilha(escolhido, false));
      setArquivo(escolhido);
    } catch (err) {
      toast.error(await mensagemDeErro(err, "Não foi possível ler a planilha"));
    } finally {
      setOcupado(false);
    }
  };

  const fechar = () => {
    setArquivo(null);
    setPrevia(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-10 px-3" disabled={ocupado} title="Planilha do cardápio (Excel)">
            {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            <span className="hidden sm:inline ml-1.5">Planilha</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem onClick={exportar} className="gap-2">
            <Download className="w-4 h-4" />
            <div>
              <div>Exportar planilha</div>
              <div className="text-[11px] text-muted-foreground">Todos os produtos, com tudo</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => entrada.current?.click()} className="gap-2">
            <Upload className="w-4 h-4" />
            <div>
              <div>Importar planilha</div>
              <div className="text-[11px] text-muted-foreground">Preço, descrição, unidade, fiscal e produtos novos</div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={entrada}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={escolher}
      />
      <ImportarCardapioDialog arquivo={arquivo} previa={previa} onFechar={fechar} onImportado={onImportado} />
    </>
  );
}
